import React, { useEffect, useRef } from "react";
import { applyRangeSelection } from "./rangeSelection";
import { ItemKey } from "./types";

type Args = {
  /** Selectable keys in rendered order — see RangeSelectionInput. */
  orderedKeys: ItemKey[];
  selectedKeys: ItemKey[];
  onChange: (keys: ItemKey[]) => void;
  /**
   * Clears the anchor when it changes — the panel's open state, nothing more.
   * Resetting on search or collapse isn't needed (a range is always a slice of
   * orderedKeys) and drops a live anchor when picking an option collapses a group.
   */
  resetKey: unknown;
};

/**
 * Owns the Shift-click anchor for a list of checkboxes. Wire to onClick, not
 * onChange: only the click carries shiftKey, and `checked` is already post-click.
 */
export function useRangeSelection({
  orderedKeys,
  selectedKeys,
  onChange,
  resetKey,
}: Args) {
  const anchorRef = useRef<ItemKey | null>(null);

  useEffect(() => {
    anchorRef.current = null;
  }, [resetKey]);

  const onCheckboxClick = (
    key: ItemKey,
    event: React.MouseEvent<HTMLInputElement>,
  ) => {
    const result = applyRangeSelection({
      orderedKeys,
      selectedKeys,
      anchorKey: anchorRef.current,
      clickedKey: key,
      shiftKey: event.shiftKey,
      selected: event.currentTarget.checked,
    });

    anchorRef.current = result.anchorKey;
    onChange(result.selectedKeys);
  };

  return { onCheckboxClick };
}
